// src/features/api/apiSlice.js
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { LogoutAction, tokenDataStore } from "../slices/authSlice"
import { Endpoints } from '../../utils/Endpoints';

const baseUrl = process.env.REACT_APP_BASE_URL
const baseQuery = fetchBaseQuery({
    baseUrl: baseUrl,
    prepareHeaders: (headers, { getState }) => {
        const token = getState().auth.tokenData?.token; // Assuming you have auth in your state

        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }
        return headers;
    },
});
// Shared in-flight refresh promise so concurrent 401s trigger a single
// refresh call instead of racing each other against a single-use refresh token.
let refreshPromise = null;

const doRefresh = (api, extraOptions) => {
    if (!refreshPromise) {
        refreshPromise = (async () => {
            const refreshToken = api.getState().auth.tokenData?.refreshToken;
            if (!refreshToken) {
                return null;
            }
            const refreshResult = await baseQuery(
                { url: Endpoints.AUTH.REFRESH_TOKEN, method: 'POST', body: { refresh_token: refreshToken } },
                api,
                extraOptions
            );
            const mainData = refreshResult.data?.data;
            if (refreshResult.data && mainData?.token) {
                api.dispatch(tokenDataStore({ refreshToken: mainData?.refreshToken, token: mainData?.token }));
                return mainData;
            }
            return null;
        })().finally(() => {
            refreshPromise = null;
        });
    }
    return refreshPromise;
};

const baseQueryWithAuth = async (args, api, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions);
    // If request fails with 401, attempt to refresh token
    // if refresh token is expired
    if (result.error && result.error.status === 403) {
        api.dispatch(LogoutAction());
    }
    // if token is expired
    if (result.error && result.error.status === 401) {
        const refreshToken = api.getState().auth.tokenData?.refreshToken;
        if (!refreshToken) {
            api.dispatch(LogoutAction());
            return result;
        }

        // Share a single refresh call across concurrent requests
        const mainData = await doRefresh(api, extraOptions);

        if (mainData) {
            // Retry the original request with new token
            return baseQuery(args, api, extraOptions);
        } else {
            // Refresh failed, force logout
            api.dispatch(LogoutAction());
        }
    }

    return result;
};

export const apiSlice = createApi({
    reducerPath: 'api',
    baseQuery: baseQueryWithAuth,
    endpoints: (builder) => ({
        dynamicQuery: builder.query({
            query: ({ endpoint, params, key, headers }) => {
                const queryParams = new URLSearchParams(params).toString();
                return {
                    url: `${endpoint}?${queryParams}`,
                    method: 'GET',
                    headers: headers || {}, // Use provided headers or an empty object
                };
            },
            providesTags: (result, error, { key }) => [key],
        }),
        dynamicMutation: builder.mutation({
            query: ({ endpoint, method = 'POST', body, headers, key }) => {
                // console.log("key@@@", key);
                // Default headers
                headers = headers || {
                    'Content-Type': 'application/json',
                };

                // Remove Content-Type header if body is a FormData object
                if (body instanceof FormData) {
                    delete headers['Content-Type'];
                }
                return {
                    url: endpoint,
                    method: method,
                    body,
                    headers: headers || {},
                }
            },
            invalidatesTags: (result, error, { key }) => {
                return key ? [key] : []
            },
        }),
    }),
});

export const { useDynamicQueryQuery, useDynamicMutationMutation } = apiSlice;
